import { Component, ElementRef, HostListener, NgZone, OnInit, OnDestroy, AfterViewInit, ViewChild } from '@angular/core';
import { lockScroll, unlockScroll } from './shared/scroll-lock';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { VersionService, SolarData, SolarHistoryPoint, ThermometerReading, DayStat, TempDataPoint, WastePickup, UnifiSnapshot, FroelingData, FroelingHistoryPoint, FroelingSettingsSnapshot, PelletDayStat, PelletUsageSummary, ZisterneData, ZisterneHistoryPoint, KnxDevicesSnapshot, KnxWeatherSnapshot } from './version.service';
import { COMMIT_HASH } from '../version';
import { CellClickedEvent, ColDef, GridOptions, ICellRendererParams } from 'ag-grid-community';
import { todayLocalISO } from './shared/format.utils';
import { CHART_RANGES, CHART_RANGE_MS, ChartRange, coveringRange } from './shared/chart.utils';
import { DeepLinkService, LINK_TABS, LinkTab } from './shared/deep-link.service';
import { leaveSheetEntry, pushSheetEntry } from './popup-card/sheet-history';
import { BoardService } from './shared/board.service';
import { uiLocale } from './shared/chart.utils';

export type LogLevel = 'all' | 'low' | 'medium' | 'high';
export type AdminSection = 'logs' | 'clients' | 'knx' | 'cams';

const LOG_LEVELS: readonly LogLevel[] = ['all', 'low', 'medium', 'high'];
const ADMIN_SECTIONS: readonly AdminSection[] = ['logs', 'clients', 'knx', 'cams'];
/** Admin overlay sections in order (phone strip and desktop sidebar; label = i18n key). */
const ADMIN_NAV: readonly { key: AdminSection; icon: string; label: string }[] = [
  { key: 'logs',    icon: '🗒',  label: 'LOGS' },
  { key: 'clients', icon: '🔌', label: 'ADMIN_CLIENTS' },
  { key: 'knx',     icon: '📡', label: 'ADMIN_KNX' },
  { key: 'cams',    icon: '📷', label: 'ADMIN_CAMS' },
];
/** Log level filter buttons (label = i18n key, tone = Bootstrap button colour). */
const LOG_LEVEL_BTNS: readonly { key: LogLevel; label: string; tone: string }[] = [
  { key: 'all',    label: 'ALLE',             tone: 'secondary' },
  { key: 'low',    label: 'ADMIN_LOG_LOW',    tone: 'secondary' },
  { key: 'medium', label: 'ADMIN_LOG_MEDIUM', tone: 'warning' },
  { key: 'high',   label: 'ADMIN_LOG_HIGH',   tone: 'danger' },
];
/** The log grid shows one wrapping "entry" column instead of six in the one-tab mobile view. */
const LOG_COMPACT_MQ = '(max-width: 767.98px)';
/** Columns sized to their content (level and stack get fixed widths: the stack's first line can be very long). */
const LOG_AUTOSIZE_COLS = ['ts', 'version', 'className'];

/** Series of the 📈 modals (the /api/history series name). */
type ChartSeries = 'solar' | 'froeling';
/** Deep-link view of each 📈 modal; it is also its chart's link id (range / zoom / selection unprefixed). */
const CHART_VIEWS: Readonly<Record<ChartSeries, string>> = { solar: 'energie/chart', froeling: 'heizung/chart' };
/** History marks of the footer overlays (phones: Back closes them, see sheet-history.ts). */
const SHEET_CHANGELOG = 'overlay=changelog', SHEET_API = 'overlay=api', SHEET_ADMIN = 'admin';

/** Mobile tab bar: the pills in order (label = i18n key). */
const NAV_TABS: readonly { key: LinkTab; icon: string; label: string }[] = [
  { key: 'energie',   icon: '⚡',  label: 'TAB_ENERGIE' },
  { key: 'klima',     icon: '🌡',  label: 'TAB_KLIMA' },
  { key: 'heizung',   icon: '🔥',  label: 'TAB_HEIZUNG' },
  { key: 'knx',       icon: '🏠',  label: 'TAB_KNX' },
  { key: 'zisterne',  icon: '💧',  label: 'TAB_WASSER' },
  { key: 'muell',     icon: '🗑️', label: 'TAB_MUELL' },
  { key: 'netzwerk',  icon: '🌐',  label: 'TAB_NETZ' },
  { key: 'statistik', icon: '📊',  label: 'TAB_STATISTIK' },
  { key: 'analyse',   icon: '📌',  label: 'TAB_ANALYSE' },
];
/** The tab bar without Analyse (no admin session and no analyse link open). */
const NAV_TABS_NO_ANALYSE = NAV_TABS.filter(t => t.key !== 'analyse');

/** Browser chrome colour per theme (= Bootstrap's body background), see index.html. */
const THEME_COLORS = { dark: '#212529', light: '#ffffff' } as const;

@Component({
  selector: 'app-dashboard',
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly title          = 'Ebersgöns Dashboard';
  readonly frontendCommit = COMMIT_HASH;
  readonly currentYear    = new Date().getFullYear();

  solar:               SolarData | null                          = null;
  thermometers:        ThermometerReading[]                      = [];
  thermometerHistory:  {[deviceId: string]: TempDataPoint[]}     = {};

  solarHistory:          SolarHistoryPoint[]    = [];
  solarChartOpen         = false;
  heizungChartOpen       = false;

  solarFlash      = false;
  knxFlash        = false;
  liveDotOn       = true;
  solarUpdatedAt: number | null   = null;
  knxUpdatedAt:   number | null   = null;
  /** KNX lights / sockets / blinds (null until the first "knxDevices" frame) and when it was received. */
  knxDevices: KnxDevicesSnapshot | null = null;
  knxDevicesUpdatedAt: number | null    = null;
  knxDevicesFlash = false;
  /** KNX weather station (null until the first "knxWeather" frame); its cards flash per changed channel. */
  knxWeather: KnxWeatherSnapshot | null = null;
  nowTick         = Date.now();
  private _liveTimer: any;
  private wakeLock: any = null;
  isOnline = navigator.onLine;
  private readonly onOnline  = () => { this.isOnline = true; };
  private readonly onOffline = () => { this.isOnline = false; };
  private readonly onPageShow = (e: PageTransitionEvent) => {
    if (e.persisted) {
      // bfcache restore: force a full fresh load with a cache-buster so the new bundle
      // is actually executed and bfcache cannot intercept the reload itself. The deep link
      // (hash, pending writes flushed by href()) is kept; DeepLinkService strips the _t again.
      this.link.href();
      location.replace(location.pathname + '?_t=' + Date.now() + location.hash);
    }
  };
  private readonly onVisibilityChange = async () => {
    if (document.visibilityState === 'visible') await this.requestWakeLock();
  };

  stats:               DayStat[]                                 = [];
  statsLoaded          = false;
  wasteLoaded          = false;
  froeling: FroelingData | null = null;
  froelingHistory: FroelingHistoryPoint[] = [];
  froelingFlash = false;
  froelingUpdatedAt: number | null = null;
  pelletStats: PelletDayStat[] = [];
  pelletUsage: PelletUsageSummary | null = null;
  /** The Fröling's settings snapshot and change log ('froelingSettings' frame). */
  froelingSettings: FroelingSettingsSnapshot | null = null;
  zisterne: ZisterneData | null = null;
  zisterneHistory: ZisterneHistoryPoint[] = [];
  zisterneFlash = false;
  zisterneUpdatedAt: number | null = null;
  unifi: UnifiSnapshot | null = null;
  unifiTimedOut = false;
  /** The tab shown on mobile: the deep link's tab (old "#heizung" links too), else Energie. A change
   *  (tap, back / forward, a view of another tab) scrolls its pill into view. */
  get activeTab(): LinkTab { return this._activeTab; }
  set activeTab(tab: LinkTab) {
    if (tab === this._activeTab) return;
    this._activeTab = tab;
    this.centerActiveTab(true);
  }
  private _activeTab: LinkTab = 'energie';
  isMobileView = window.innerWidth < 768;

  /** The Analyse tab / desktop entry: with an admin session (its slots), or while an analyse link is open
   *  (a shared read-only snapshot; a guest's note). */
  get showAnalyse(): boolean { return this.board.admin || this.link.tab === 'analyse'; }

  /** The mobile tab bar's pills (Analyse only when {@link showAnalyse}). */
  get navTabs(): readonly { key: LinkTab; icon: string; label: string }[] {
    return this.showAnalyse ? NAV_TABS : NAV_TABS_NO_ANALYSE;
  }

  /** Mobile tab nav: a new history entry (Back returns to the previous tab), any open view closes. The
   *  page starts at the top (the bar is sticky, the old tab's scroll offset means nothing for the new one). */
  setTab(tab: LinkTab): void {
    if (this.activeTab === tab && this.link.tab === tab) return;   // keeps a sub-view (e.g. knx/lights)
    this.activeTab = tab;
    this.link.setTab(tab);
    if (window.scrollY > 0) window.scrollTo({ top: 0 });
  }

  /** The Analyse board's "← Dashboard": back to the tab shown before it (desktop: scrolled to its section). */
  leaveBoard(tab: LinkTab): void {
    this.setTab(tab);
    this.scrollToSection();
  }

  @HostListener('window:resize')
  onResize() {
    const mobile = window.innerWidth < 768;
    if (mobile && !this.isMobileView) this.centerActiveTab();   // the tab bar just appeared
    this.isMobileView = mobile;
    this.updateTabFades();
  }

  /** The pill strip of the mobile tab bar (always rendered; hidden from 768 px by CSS). */
  @ViewChild('tabStrip') private tabStrip?: ElementRef<HTMLElement>;
  private tabCenterFrame = 0;
  private readonly onTabStripScroll = () => this.updateTabFades();

  /** Scroll the active pill to the middle of the strip (after the next layout: labels may just have changed). */
  private centerActiveTab(smooth = false): void {
    this.zone.runOutsideAngular(() => {
      cancelAnimationFrame(this.tabCenterFrame);
      this.tabCenterFrame = requestAnimationFrame(() => {
        const strip = this.tabStrip?.nativeElement;
        const pill = strip?.querySelector<HTMLElement>(`[data-tab="${this._activeTab}"]`);
        if (!strip || !pill || !strip.clientWidth) return;
        const s = strip.getBoundingClientRect(), p = pill.getBoundingClientRect();
        const left = strip.scrollLeft + (p.left + p.width / 2) - (s.left + s.width / 2);
        const motion = smooth && !matchMedia('(prefers-reduced-motion: reduce)').matches;
        strip.scrollTo({ left, behavior: motion ? 'smooth' : 'auto' });
        this.updateTabFades();
      });
    });
  }

  /** Fade masks at the strip's edges while more pills are hidden there (styles.css: .dash-tabs-strip). */
  private updateTabFades(): void {
    const strip = this.tabStrip?.nativeElement;
    if (!strip) return;
    const max = strip.scrollWidth - strip.clientWidth;
    strip.classList.toggle('fade-start', strip.scrollLeft > 2);
    strip.classList.toggle('fade-end', strip.scrollLeft < max - 2);
  }

  wastePickups:        WastePickup[]                             = [];
  nextPickupsByType:   { type: string; name: string; date: string; icon: string }[] = [];
  logs:                any[]                                     = [];
  logLevel: LogLevel         = 'all';
  adminOpen    = false;
  adminSection: AdminSection = 'logs';
  readonly adminNav     = ADMIN_NAV;
  readonly logLevelBtns = LOG_LEVEL_BTNS;
  /** The log grid's column set: compact (one wrapping column) below 768 px. */
  private logCompact = matchMedia(LOG_COMPACT_MQ).matches;
  clients: { id: string; ip: string; userAgent: string; connectedAt: number; xForwardedFor: string; xRealIp: string; remoteAddr: string }[] = [];
  darkMode  = false;

  colDefs: ColDef[] = [];

  readonly gridOptions: GridOptions = {
    animateRows: true,
    rowHeight: 32,
    headerHeight: 36,
    enableCellTextSelection: true,
    ensureDomOrder: true,
    onFirstDataRendered: (e) => {
      if (!this.logCompact) e.api.autoSizeColumns(LOG_AUTOSIZE_COLS);
    },
    // Crossing 768 px (rotation, window resize; also while the overlay was closed): switch between the
    // compact and the full column set, and size the full set once its cells are rendered
    onGridReady: (e) => {
      const mq = matchMedia(LOG_COMPACT_MQ);
      const apply = () => this.zone.run(() => {
        if (mq.matches === this.logCompact || e.api.isDestroyed()) return;
        this.logCompact = mq.matches;
        this.buildLogColDefs();
        e.api.setGridOption('columnDefs', this.colDefs);
        if (!this.logCompact) setTimeout(() => { if (!e.api.isDestroyed()) e.api.autoSizeColumns(LOG_AUTOSIZE_COLS); }, 50);
      });
      mq.addEventListener('change', apply);
      e.api.addEventListener('gridPreDestroyed', () => mq.removeEventListener('change', apply));
      apply();
    },
    onCellDoubleClicked: (e) => {
      // The compact entry column copies the message and stack, not its sort key (the time)
      const val = e.column.getColId() === 'entry' ? [e.data?.message, e.data?.stack].filter(Boolean).join('\n')
                : e.value != null ? String(e.value) : '';
      navigator.clipboard.writeText(val).then(() => {
        this.copiedCell = true;
        setTimeout(() => { this.copiedCell = false; }, 1200);
      }).catch(() => {});
    },
  };

  readonly location   = location;
  copiedCell          = false;
  /** Log entries whose stack is shown in full (the entry objects: stable across sorting and filtering). */
  expandedStackRows   = new Set<object>();
  langPickerOpen  = false;
  langSearch      = '';
  changelogOpen   = false;
  changelog: { hash: string; subject: string; date: string; author: string }[] = [];
  apiDocsOpen     = false;
  apiResources: { name: string; description: string; auth: boolean; wsType: string; url: string }[] = [];

  readonly languages = [
    { code: 'en', flag: '🇬🇧', name: 'English',    native: 'English' },
    { code: 'de', flag: '🇩🇪', name: 'German',     native: 'Deutsch' },
    { code: 'fr', flag: '🇫🇷', name: 'French',     native: 'Français' },
    { code: 'es', flag: '🇪🇸', name: 'Spanish',    native: 'Español' },
    { code: 'pt', flag: '🇵🇹', name: 'Portuguese', native: 'Português' },
    { code: 'it', flag: '🇮🇹', name: 'Italian',    native: 'Italiano' },
    { code: 'nl', flag: '🇳🇱', name: 'Dutch',      native: 'Nederlands' },
    { code: 'pl', flag: '🇵🇱', name: 'Polish',     native: 'Polski' },
    { code: 'ru', flag: '🇷🇺', name: 'Russian',    native: 'Русский' },
    { code: 'uk', flag: '🇺🇦', name: 'Ukrainian',  native: 'Українська' },
    { code: 'tr', flag: '🇹🇷', name: 'Turkish',    native: 'Türkçe' },
    { code: 'ar', flag: '🇸🇦', name: 'Arabic',     native: 'العربية' },
    { code: 'he', flag: '🇮🇱', name: 'Hebrew',     native: 'עברית' },
    { code: 'fa', flag: '🇮🇷', name: 'Persian',    native: 'فارسی' },
    { code: 'zh', flag: '🇨🇳', name: 'Chinese',    native: '中文' },
    { code: 'ja', flag: '🇯🇵', name: 'Japanese',   native: '日本語' },
    { code: 'ko', flag: '🇰🇷', name: 'Korean',     native: '한국어' },
    { code: 'hi', flag: '🇮🇳', name: 'Hindi',      native: 'हिन्दी' },
    { code: 'bn', flag: '🇧🇩', name: 'Bengali',    native: 'বাংলা' },
    { code: 'ur', flag: '🇵🇰', name: 'Urdu',       native: 'اردو' },
    { code: 'id', flag: '🇮🇩', name: 'Indonesian', native: 'Bahasa Indonesia' },
    { code: 'ms', flag: '🇲🇾', name: 'Malay',      native: 'Bahasa Melayu' },
    { code: 'th', flag: '🇹🇭', name: 'Thai',       native: 'ภาษาไทย' },
    { code: 'vi', flag: '🇻🇳', name: 'Vietnamese', native: 'Tiếng Việt' },
    { code: 'sv', flag: '🇸🇪', name: 'Swedish',    native: 'Svenska' },
    { code: 'no', flag: '🇳🇴', name: 'Norwegian',  native: 'Norsk' },
    { code: 'da', flag: '🇩🇰', name: 'Danish',     native: 'Dansk' },
    { code: 'fi', flag: '🇫🇮', name: 'Finnish',    native: 'Suomi' },
    { code: 'cs', flag: '🇨🇿', name: 'Czech',      native: 'Čeština' },
    { code: 'sk', flag: '🇸🇰', name: 'Slovak',     native: 'Slovenčina' },
    { code: 'hu', flag: '🇭🇺', name: 'Hungarian',  native: 'Magyar' },
    { code: 'ro', flag: '🇷🇴', name: 'Romanian',   native: 'Română' },
    { code: 'bg', flag: '🇧🇬', name: 'Bulgarian',  native: 'Български' },
    { code: 'hr', flag: '🇭🇷', name: 'Croatian',   native: 'Hrvatski' },
    { code: 'sr', flag: '🇷🇸', name: 'Serbian',    native: 'Српски' },
    { code: 'el', flag: '🇬🇷', name: 'Greek',      native: 'Ελληνικά' },
    { code: 'sw', flag: '🇰🇪', name: 'Swahili',    native: 'Kiswahili' },
    { code: 'am', flag: '🇪🇹', name: 'Amharic',    native: 'አማርኛ' },
    { code: 'af', flag: '🇿🇦', name: 'Afrikaans',  native: 'Afrikaans' },
    { code: 'ca', flag: '🇦🇩', name: 'Catalan',    native: 'Català' },
    { code: 'eu', flag: '🏴', name: 'Basque',      native: 'Euskara' },
    { code: 'gl', flag: '🇪🇸', name: 'Galician',   native: 'Galego' },
    { code: 'lt', flag: '🇱🇹', name: 'Lithuanian', native: 'Lietuvių' },
    { code: 'lv', flag: '🇱🇻', name: 'Latvian',    native: 'Latviešu' },
    { code: 'et', flag: '🇪🇪', name: 'Estonian',   native: 'Eesti' },
    { code: 'sl', flag: '🇸🇮', name: 'Slovenian',  native: 'Slovenščina' },
    { code: 'mk', flag: '🇲🇰', name: 'Macedonian', native: 'Македонски' },
    { code: 'sq', flag: '🇦🇱', name: 'Albanian',   native: 'Shqip' },
    { code: 'ka', flag: '🇬🇪', name: 'Georgian',   native: 'ქართული' },
    { code: 'hy', flag: '🇦🇲', name: 'Armenian',   native: 'Հայերեն' },
    { code: 'az', flag: '🇦🇿', name: 'Azerbaijani',native: 'Azərbaycan' },
    { code: 'kk', flag: '🇰🇿', name: 'Kazakh',     native: 'Қазақша' },
    { code: 'uz', flag: '🇺🇿', name: 'Uzbek',      native: 'Oʻzbekcha' },
    { code: 'mn', flag: '🇲🇳', name: 'Mongolian',  native: 'Монгол' },
    { code: 'ne', flag: '🇳🇵', name: 'Nepali',     native: 'नेपाली' },
    { code: 'si', flag: '🇱🇰', name: 'Sinhala',    native: 'සිංහල' },
    { code: 'my', flag: '🇲🇲', name: 'Burmese',    native: 'မြန်မာ' },
    { code: 'km', flag: '🇰🇭', name: 'Khmer',      native: 'ភាសាខ្មែរ' },
    { code: 'lo', flag: '🇱🇦', name: 'Lao',        native: 'ລາວ' },
    { code: 'ta', flag: '🇮🇳', name: 'Tamil',      native: 'தமிழ்' },
    { code: 'te', flag: '🇮🇳', name: 'Telugu',     native: 'తెలుగు' },
    { code: 'ml', flag: '🇮🇳', name: 'Malayalam',  native: 'മലയാളം' },
    { code: 'mr', flag: '🇮🇳', name: 'Marathi',    native: 'मराठी' },
    { code: 'gu', flag: '🇮🇳', name: 'Gujarati',   native: 'ગુજરાતી' },
    { code: 'pa', flag: '🇮🇳', name: 'Punjabi',    native: 'ਪੰਜਾਬੀ' },
    { code: 'ha', flag: '🇳🇬', name: 'Hausa',      native: 'Hausa' },
    { code: 'yo', flag: '🇳🇬', name: 'Yoruba',     native: 'Yorùbá' },
    { code: 'ig', flag: '🇳🇬', name: 'Igbo',       native: 'Igbo' },
    { code: 'zu', flag: '🇿🇦', name: 'Zulu',       native: 'IsiZulu' },
    { code: 'xh', flag: '🇿🇦', name: 'Xhosa',      native: 'IsiXhosa' },
    { code: 'so', flag: '🇸🇴', name: 'Somali',     native: 'Soomaali' },
    { code: 'ht', flag: '🇭🇹', name: 'Haitian Creole', native: 'Kreyòl Ayisyen' },
    { code: 'tl', flag: '🇵🇭', name: 'Filipino',   native: 'Filipino' },
    { code: 'jv', flag: '🇮🇩', name: 'Javanese',   native: 'Basa Jawa' },
    { code: 'cy', flag: '🏴󠁧󠁢󠁷󠁬󠁳󠁿', name: 'Welsh',       native: 'Cymraeg' },
    { code: 'ga', flag: '🇮🇪', name: 'Irish',      native: 'Gaeilge' },
    { code: 'is', flag: '🇮🇸', name: 'Icelandic',  native: 'Íslenska' },
    { code: 'lb', flag: '🇱🇺', name: 'Luxembourgish', native: 'Lëtzebuergesch' },
    { code: 'mt', flag: '🇲🇹', name: 'Maltese',    native: 'Malti' },
  ];

  readonly supportedLangs = new Set([
    'af','am','ar','az','bg','bn','ca','cs','cy','da','de','el','en','es','et',
    'eu','fa','fi','fr','ga','gl','gu','ha','he','hi','hr','ht','hu','hy','id',
    'ig','is','it','ja','jv','ka','kk','km','ko','lb','lo','lt','lv','mk','ml',
    'mn','mr','ms','mt','my','ne','nl','no','pa','pl','pt','ro','ru','si','sk',
    'sl','so','sq','sr','sv','sw','ta','te','th','tl','tr','uk','ur','uz','vi',
    'xh','yo','zh','zu'
  ]);

  get filteredLanguages() {
    const q = this.langSearch.toLowerCase();
    if (!q) return this.languages;
    return this.languages.filter(l =>
      l.name.toLowerCase().includes(q) || l.native.toLowerCase().includes(q)
    );
  }

  get currentLangFlag(): string {
    return this.languages.find(l => l.code === this.translate.currentLang)?.flag ?? '🌐';
  }

  logPassword      = '';
  /** Second factor: the 6-digit TOTP code, sent together with the password (never stored). */
  logCode          = '';
  logAuthError     = false;
  /** Login answered 429: too many failed attempts (per IP or globally). */
  logThrottled     = false;
  /** Login failed for another reason than the credentials (5xx: ADMIN_PW_HASH / TOTP secret invalid, server busy, network): i18n key + detail. */
  logLoginError: { key: string; msg: string } | null = null;
  logAuthenticated = false;
  /** Backend still accepts the legacy hard-coded admin password (ADMIN_PW_HASH unset): banner in the admin header. */
  adminLegacyPassword = false;
  /**
   * Public GET /api/admin/login: the login needs a TOTP code. null = not known yet (the code field is shown);
   * false while logged in = the backend has no TOTP secret: banner in the admin header.
   */
  adminTotp: boolean | null = null;
  /** A login request is running (the backend's check takes ~0.5 s): no second submit meanwhile. */
  logLoggingIn = false;
  /** The session token survives browser restarts: a session lasts 14 days (backend), logout or a 401 ends it.
   *  The Analyse slots (BoardService) follow it: every change of the token is handed on. */
  private _adminToken = localStorage.getItem('adminSessionToken') ?? '';
  private get adminToken(): string { return this._adminToken; }
  private set adminToken(token: string) {
    if (token === this._adminToken) return;
    this._adminToken = token;
    this.board.setAdminAuth(token ? this.adminHeadersFn : null);
  }

  private subs: Subscription[] = [];
  private _nowTimer: any;
  /** Desktop: stops re-scrolling to a linked section (see scrollToSection). */
  private stopSectionScroll: (() => void) | null = null;
  /** Desktop: a scroll lets the link's tab follow the section in view (see syncSectionTab), once per frame. */
  private sectionSyncFrame = 0;
  private readonly onWindowScroll = () => {
    if (this.sectionSyncFrame) return;
    this.sectionSyncFrame = requestAnimationFrame(() => { this.sectionSyncFrame = 0; this.syncSectionTab(); });
  };

  constructor(
    readonly vs: VersionService,
    readonly translate: TranslateService,
    private readonly link: DeepLinkService,
    private readonly zone: NgZone,
    readonly board: BoardService,
  ) {
    translate.addLangs(['en', 'de']);
    translate.setDefaultLang('en');

    this.darkMode = localStorage.getItem('darkMode') !== 'false';
    this.applyDarkMode();
    this._activeTab = link.tab ?? 'energie';
    // the Analyse slots need the admin session (a 401 there drops a stale token quietly, like the KNX rooms')
    board.setAdminAuth(this.adminToken ? this.adminHeadersFn : null, () => this.onKnxAdminUnauthorized());
  }

  /** Restart a thermometer / KNX devices section's highlight animation. */
  private flash(key: 'knxFlash' | 'knxDevicesFlash'): void {
    this[key] = false;
    setTimeout(() => { this[key] = true; setTimeout(() => { this[key] = false; }, 900); }, 0);
  }

  toggleDarkMode(): void {
    this.darkMode = !this.darkMode;
    localStorage.setItem('darkMode', String(this.darkMode));
    this.applyDarkMode();
  }

  private applyDarkMode(): void {
    document.documentElement.setAttribute('data-bs-theme', this.darkMode ? 'dark' : 'light');
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', this.darkMode ? THEME_COLORS.dark : THEME_COLORS.light);
  }

  ngOnInit(): void {
    const saved = localStorage.getItem('lang') ?? 'en';
    this.translate.use(saved).subscribe(() => this.buildLogColDefs());

    // On a language change: log column headers, <html lang> (phones hyphenate long German words),
    // the tab pills' new widths
    this.subs.push(
      this.translate.onLangChange.subscribe(e => {
        this.buildLogColDefs();
        document.documentElement.lang = e.lang;
        this.centerActiveTab();
      })
    );

    this._liveTimer = setInterval(() => { this.liveDotOn = !this.liveDotOn; }, 600);
    this._nowTimer  = setInterval(() => { this.nowTick = Date.now(); }, 1000);

    this.vs.connect(this.frontendCommit);
    setTimeout(() => { if (this.unifi === null) this.unifiTimedOut = true; }, 10_000);

    this.subs.push(
      this.vs.solarHistory$.subscribe(pts => { this.solarHistory = pts; }),
      this.vs.solar$.subscribe(data => {
        this.solar = data;
        this.solarUpdatedAt = Date.now();
        const now = Date.now();
        const cutoff = now - 24 * 3600 * 1000;
        const pt: SolarHistoryPoint = { ts: now, pvYieldW: data.pvYieldW, batteryDischargeW: data.batteryDischargeW, gridConsumptionW: data.gridConsumptionW, totalConsumptionW: data.totalConsumptionW, batteryPercent: data.batteryPercent, consumptionL1W: data.consumptionL1W ?? 0, consumptionL2W: data.consumptionL2W ?? 0, consumptionL3W: data.consumptionL3W ?? 0 };
        this.solarHistory = [...this.solarHistory.filter(p => p.ts >= cutoff), pt];
        this.solarFlash = false;
        setTimeout(() => { this.solarFlash = true; setTimeout(() => { this.solarFlash = false; }, 900); }, 0);
      }),
      // Each frame carries ALL readings (the backend still polls Govee too, the UI shows only KNX):
      // "updated ago" starts on first sight (handshake) and is then refreshed (and flashed) only by
      // new KNX points, so a stalled KNX poller shows its age.
      this.vs.thermometers$.subscribe(data => {
        this.thermometers = data;
        if (this.knxUpdatedAt   === null && data.some(t => t.source === 'knx')) { this.knxUpdatedAt   = Date.now(); this.flash('knxFlash'); }
      }),
      this.vs.thermometerHistory$.subscribe(h => { this.thermometerHistory = h; }),
      // Each poller sends only the points it just recorded: append them to the
      // 24h window as a new object so the Klima / KNX charts' OnChanges fire.
      this.vs.thermometerAppend$.subscribe(append => {
        const cutoff = Date.now() - 24 * 3600 * 1000;
        const next = { ...this.thermometerHistory };
        for (const [deviceId, pt] of Object.entries(append)) {
          const prev = next[deviceId] ?? [];
          if (prev.length && pt.ts <= prev[prev.length - 1].ts) continue;
          next[deviceId] = [...prev.filter(p => p.ts >= cutoff), pt];
        }
        this.thermometerHistory = next;
        const fresh = this.thermometers.filter(t => t.deviceId in append);
        if (fresh.some(t => t.source === 'knx')) { this.knxUpdatedAt   = Date.now(); this.flash('knxFlash'); }
      }),
      this.vs.stats$.subscribe(data => { this.stats = data; this.statsLoaded = true; }),
      this.vs.waste$.subscribe(data => {
        this.wastePickups = data;
        this.nextPickupsByType = this._computeNextPickups(data);
        this.wasteLoaded = true;
      }),
      this.vs.unifi$.subscribe(data => { this.unifi = data; this.unifiTimedOut = false; }),
      this.vs.zisterneHistory$.subscribe(pts => { this.zisterneHistory = pts; }),
      this.vs.zisterne$.subscribe(data => {
        this.zisterne = data;
        this.zisterneUpdatedAt = Date.now();
        const now = Date.now();
        const cutoff = now - 7 * 24 * 3600 * 1000;
        const pt: ZisterneHistoryPoint = { ts: now, levelPct: data.levelPct, depthCm: data.depthCm };
        this.zisterneHistory = [...this.zisterneHistory.filter(p => p.ts >= cutoff), pt];
        this.zisterneFlash = false;
        setTimeout(() => { this.zisterneFlash = true; setTimeout(() => { this.zisterneFlash = false; }, 900); }, 0);
      }),
      this.vs.froelingHistory$.subscribe(pts => { this.froelingHistory = pts; }),
      this.vs.froeling$.subscribe(data => {
        this.froeling = data;
        this.froelingUpdatedAt = Date.now();
        const now = Date.now();
        const cutoff = now - 24 * 3600 * 1000;
        const pt: FroelingHistoryPoint = {
          ts: now,
          boilerTempC: data.boilerTempC,
          exhaustTempC: data.exhaustTempC,
          boilerModulationPct: data.boilerModulationPct,
          bufferTopTempC: data.bufferTopTempC,
          bufferBottomTempC: data.bufferBottomTempC,
          bufferChargePct: data.bufferChargePct,
          hotWaterTempC: data.hotWaterTempC,
          flowTempActualC: data.flowTempActualC,
          outdoorTempC: data.outdoorTempC,
          solarThermalTempC: data.solarThermalTempC,
          pelletConsumptionKg: data.pelletConsumptionKg,
          pelletLevelPct: data.pelletLevelPct,
          circulationPumpPct: data.circulationPumpPct,
          heatingCircuits: data.heatingCircuits.map(hc => ({ name: hc.name, flowTempActualC: hc.flowTempActualC, flowTempTargetC: hc.flowTempTargetC, pumpState: hc.pumpState })),
          // the later fields, as the backend archives them (FroelingHistoryPoint)
          boilerStateCode: data.boilerStateCode,
          draftFanPct: data.boilerStateCode != null ? Math.round(data.draftFanPct) : undefined,
          oxygenPct: data.boilerStateCode != null ? data.oxygenPct : undefined,
          ashHoursLeft: data.ashHoursLeft,
          bufferPumpPct: data.bufferPumpPct,
          dhwPumpPct: data.dhwPumpPct,
          dhwSolarRefTempC: data.dhwSolarRefTempC,
          solarThermalPumpPct: data.collectorFlowTempC != null ? Math.round(data.solarThermalPumpPct) : undefined,
          collectorFlowTempC: data.collectorFlowTempC,
          collectorReturnTempC: data.collectorReturnTempC,
          solarBufferTopTempC: data.solarBufferTopTempC,
          solarBufferBottomTempC: data.solarBufferBottomTempC,
          circulationReturnTempC: data.circulationReturnTempC,
          circulationFlowSwitch: data.circulationFlowSwitch,
          storeRemainingT: data.storeRemainingT,
        };
        this.froelingHistory = [...this.froelingHistory.filter(p => p.ts >= cutoff), pt];
        this.froelingFlash = false;
        setTimeout(() => { this.froelingFlash = true; setTimeout(() => { this.froelingFlash = false; }, 900); }, 0);
      }),
      this.vs.pelletStats$.subscribe(days => { this.pelletStats = days; }),
      this.vs.pelletUsage$.subscribe(u => { this.pelletUsage = u; }),
      this.vs.froelingSettings$.subscribe(s => { this.froelingSettings = s; }),
      // Pushed on connect, on every change and as a 5 min heartbeat: always a fresh snapshot object.
      this.vs.knxDevices$.subscribe(snap => {
        this.knxDevices = snap;
        this.knxDevicesUpdatedAt = Date.now();
        this.flash('knxDevicesFlash');
      }),
      // Pushed on connect and on change (at most every 10 s): always a fresh snapshot object.
      this.vs.knxWeather$.subscribe(snap => { this.knxWeather = snap; }),
    );
    this.requestWakeLock();
    window.addEventListener('online',   this.onOnline);
    window.addEventListener('offline',  this.onOffline);
    window.addEventListener('pageshow', this.onPageShow);
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    // Deep link: open what the link names (the 📈 modals, footer overlays, admin) — now and whenever
    // the browser navigates (back / forward, an edited link).
    this.applyLink();
    this.subs.push(
      this.link.external$.subscribe(() => {
        this.activeTab = this.link.tab ?? 'energie';
        this.applyLink();
        this.scrollToSection();
      }),
      // A view of another tab was opened (desktop shows every tab's popups): the tab follows it.
      // May fire outside Angular (a legend click in a chart): re-enter only for a real change.
      this.link.changed$.subscribe(() => {
        const tab = this.link.tab;
        if (tab && tab !== this.activeTab) this.zone.run(() => { this.activeTab = tab; });
      }),
    );
  }

  ngAfterViewInit(): void {
    // If the page was hidden during a document.write() swap to prevent the
    // empty-app-root flash, reveal it now that Angular has fully rendered.
    document.documentElement.style.removeProperty('visibility');
    this.scrollToSection();
    this.zone.runOutsideAngular(() => {
      window.addEventListener('scroll', this.onWindowScroll, { passive: true });
      this.tabStrip?.nativeElement.addEventListener('scroll', this.onTabStripScroll, { passive: true });
    });
    this.centerActiveTab();
    document.fonts?.ready.then(() => this.centerActiveTab());   // the emoji font changes the pill widths
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    clearInterval(this._liveTimer);
    clearInterval(this._nowTimer);
    this.stopSectionScroll?.();
    window.removeEventListener('scroll', this.onWindowScroll);
    cancelAnimationFrame(this.sectionSyncFrame);
    this.tabStrip?.nativeElement.removeEventListener('scroll', this.onTabStripScroll);
    cancelAnimationFrame(this.tabCenterFrame);
    window.removeEventListener('online',   this.onOnline);
    window.removeEventListener('offline',  this.onOffline);
    window.removeEventListener('pageshow', this.onPageShow);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.wakeLock?.release?.();
  }

  // ── Deep link ──────────────────────────────────────────────────────────────

  /** Open / close the shell's modals and overlays as the link says (on init and on back / forward). */
  private applyLink(): void {
    const solar   = this.link.isOpen(CHART_VIEWS.solar);
    const heizung = this.link.isOpen(CHART_VIEWS.froeling);
    if (!solar)   this.hideChartModal('solar');
    if (!heizung) this.hideChartModal('froeling');
    if (solar)    this.showChartModal('solar');
    if (heizung)  this.showChartModal('froeling');

    const overlay = this.link.get('overlay');
    if (overlay === 'changelog') this.showChangelog(); else this.hideChangelog();
    if (overlay === 'api')       this.showApiDocs();   else this.hideApiDocs();

    this.applyAdminLink();
  }

  /**
   * Desktop shows every tab's section stacked: bring the linked tab's section into view (on load and
   * on back / forward; not on the app's own changes). The sections above it keep growing while their
   * data arrives, so it re-scrolls on every layout change for a few seconds — until the user scrolls.
   */
  private scrollToSection(): void {
    this.stopSectionScroll?.();
    const tab = this.link.tab;
    if (!tab || this.isMobileView) return;
    this.zone.runOutsideAngular(() => {
      const go = () => document.getElementById('sec-' + tab)?.scrollIntoView({ block: 'start' });
      const userEvents = ['wheel', 'touchstart', 'keydown', 'mousedown'];
      const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => go()) : null;
      const timer = setTimeout(() => stop(), 8000);
      const frame = requestAnimationFrame(go);
      const stop = () => {
        ro?.disconnect();
        clearTimeout(timer);
        cancelAnimationFrame(frame);
        userEvents.forEach(ev => window.removeEventListener(ev, stop));
        if (this.stopSectionScroll === stop) this.stopSectionScroll = null;
      };
      ro?.observe(document.body);
      userEvents.forEach(ev => window.addEventListener(ev, stop, { passive: true }));
      this.stopSectionScroll = stop;
    });
  }

  /**
   * Desktop has no tab nav — every section is shown: after a scroll the section in view becomes the link's
   * tab, so a reload or a copied link comes back to it (scrollToSection) instead of to the tab of the last
   * popup. Not while a view is open (a popup, a KNX sub-tab: its path is what the link shows) nor while a
   * linked section is being scrolled to. `openView` with the bare tab replaces the history entry and keeps
   * every key (the path is already empty, so every chart key is prefixed). Runs outside Angular.
   */
  private syncSectionTab(): void {
    if (this.isMobileView || this.stopSectionScroll || this.link.view) return;
    const tab = this.sectionInView();
    if (tab !== (this.link.tab ?? 'energie')) this.link.openView(tab);
  }

  /** The section the user looks at: the linked one while it still fills the top of the viewport (or the
   *  page is at its end, where a short last section cannot scroll to the top), else the last one that
   *  starts above 30 % of the viewport height. */
  private sectionInView(): LinkTab {
    const current: LinkTab = this.link.tab ?? 'energie';
    const probe = window.innerHeight * 0.3;
    const cur = document.getElementById('sec-' + current)?.getBoundingClientRect();
    const atEnd = window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 2;
    if (cur && cur.height > 0 && cur.top <= probe && cur.bottom > 80) return current;
    if (cur && cur.height > 0 && atEnd && cur.top < window.innerHeight && cur.bottom > 0) return current;
    let best: { tab: LinkTab; top: number } = { tab: 'energie', top: -Infinity };
    for (const t of LINK_TABS) {
      const r = document.getElementById('sec-' + t)?.getBoundingClientRect();
      if (r && r.height > 0 && r.top <= probe && r.top > best.top) best = { tab: t, top: r.top };
    }
    return best.tab;
  }

  private async requestWakeLock(): Promise<void> {
    if ('wakeLock' in navigator) {
      try { this.wakeLock = await (navigator as any).wakeLock.request('screen'); }
      catch (_) {}
    }
  }

  /** Footer: the changelog overlay (link: overlay=changelog). */
  openChangelog(): void {
    if (!this.changelogOpen) pushSheetEntry(SHEET_CHANGELOG, this.link, this.zone);
    this.link.set('overlay', 'changelog');
    this.showChangelog();
  }

  closeChangelog(): void {
    if (this.link.get('overlay') === 'changelog') this.link.set('overlay', null);
    this.hideChangelog();
    leaveSheetEntry(SHEET_CHANGELOG);
  }

  private showChangelog(): void {
    if (this.changelogOpen) return;
    this.changelogOpen = true;
    lockScroll();
    if (this.changelog.length === 0) {
      fetch('/api/changelog')
        .then(r => r.json())
        .then(data => { this.changelog = data; })
        .catch(() => {});
    }
  }

  private hideChangelog(): void {
    if (this.changelogOpen) { this.changelogOpen = false; unlockScroll(); }
  }

  /** Footer: the API documentation overlay (link: overlay=api). */
  openApiDocs(): void {
    if (!this.apiDocsOpen) pushSheetEntry(SHEET_API, this.link, this.zone);
    this.link.set('overlay', 'api');
    this.showApiDocs();
  }

  closeApiDocs(): void {
    if (this.link.get('overlay') === 'api') this.link.set('overlay', null);
    this.hideApiDocs();
    leaveSheetEntry(SHEET_API);
  }

  private showApiDocs(): void {
    if (this.apiDocsOpen) return;
    this.apiDocsOpen = true;
    lockScroll();
    if (this.apiResources.length === 0) {
      fetch('/api/data')
        .then(r => r.json())
        .then(data => { this.apiResources = data; })
        .catch(() => {});
    }
  }

  private hideApiDocs(): void {
    if (this.apiDocsOpen) { this.apiDocsOpen = false; unlockScroll(); }
  }

  openLangPicker(): void {
    if (this.langPickerOpen) return;
    this.langPickerOpen = true;
    this.langSearch = '';
    lockScroll();
  }

  closeLangPicker(): void {
    if (this.langPickerOpen) { this.langPickerOpen = false; unlockScroll(); }
  }

  // ── 📈 modals (Energie: solar, Heizung: Fröling) and their time range ─────
  // 'day' uses the live 24h history pushed over WS; longer ranges fetch the downsampled long-term
  // archive from /api/history. Each modal is a deep-link view ('energie/chart', 'heizung/chart')
  // with its own range / zoom / series selection in the link; at most one is open.
  /** Range of the open modal (the range whose data it shows). */
  chartRange: ChartRange = 'day';
  rangeLoading = false;
  solarRangeHistory:    SolarHistoryPoint[]    = [];
  froelingRangeHistory: FroelingHistoryPoint[] = [];
  /** Range buttons; labels are i18n keys (RANGE_DAY …). */
  readonly chartRanges = CHART_RANGES;
  /** The modals' view paths = their charts' link ids. */
  readonly chartViews = CHART_VIEWS;
  /** Only the newest archive request may fill the chart (fast range clicks, back / forward). */
  private rangeSeq = 0;

  get solarChartHistory(): SolarHistoryPoint[] {
    return this.chartRange === 'day' ? this.solarHistory : this.solarRangeHistory;
  }
  get froelingChartHistory(): FroelingHistoryPoint[] {
    return this.chartRange === 'day' ? this.froelingHistory : this.froelingRangeHistory;
  }

  get rangeWindowMs(): number {
    return CHART_RANGE_MS[this.chartRange];
  }

  /** Range button of a modal: into the link (a new range drops the zoom window), then load it. */
  setChartRange(range: ChartRange, series: ChartSeries): void {
    this.link.setRange(CHART_VIEWS[series], range);
    this.loadChartRange(range, series);
  }

  private loadChartRange(range: ChartRange, series: ChartSeries): void {
    const seq = ++this.rangeSeq;
    this.chartRange = range;
    if (range === 'day') { this.rangeLoading = false; return; }   // live data already in memory
    const to   = Date.now();
    const from = to - CHART_RANGE_MS[range];
    this.rangeLoading = true;
    fetch(`/api/history?series=${series}&from=${from}&to=${to}&buckets=600`)
      .then(r => r.json())
      .then((pts: any[]) => {
        if (seq !== this.rangeSeq) return;
        if (series === 'solar') this.solarRangeHistory = pts ?? [];
        else                    this.froelingRangeHistory = pts ?? [];
        this.rangeLoading = false;
      })
      .catch(() => { if (seq === this.rangeSeq) this.rangeLoading = false; });
  }

  /** 📈 in the Energie tab. */
  openSolarChart(): void { this.openChartModal('solar'); }
  closeSolarChart(): void { this.closeChartModal('solar'); }

  /** 📈 in the Heizung tab. */
  openHeizungChart(): void { this.openChartModal('froeling'); }
  closeHeizungChart(): void { this.closeChartModal('froeling'); }

  /** 📈 tapped: the modal's view into the link (phones: in a history entry of its own, so Back closes it). */
  private openChartModal(series: ChartSeries): void {
    const view = CHART_VIEWS[series];
    if (!this.link.isOpen(view)) pushSheetEntry(view, this.link, this.zone);
    this.link.openView(view);
    this.showChartModal(series);
  }

  private closeChartModal(series: ChartSeries): void {
    const view = CHART_VIEWS[series];
    this.link.closeView(view);
    this.hideChartModal(series);
    leaveSheetEntry(view);
  }

  /** Open a modal (idempotent) with the link's range — widened so a linked zoom window lies inside
   *  the loaded data (a link opened days later still shows its window); the widened range is written
   *  back, so the buttons, the URL and a later Reset Zoom agree. */
  private showChartModal(series: ChartSeries): void {
    const view = CHART_VIEWS[series];
    const wasOpen = series === 'solar' ? this.solarChartOpen : this.heizungChartOpen;
    if (!wasOpen) {
      if (series === 'solar') { this.solarChartOpen = true; this.solarRangeHistory = []; }
      else                    { this.heizungChartOpen = true; this.froelingRangeHistory = []; }
      lockScroll();
    }
    const linked = this.link.range(view) ?? 'day';
    const range = coveringRange(linked, this.link.zoom(view));
    if (range !== linked) this.link.set(this.link.chartKey(view, 'range'), range === 'day' ? null : range);
    if (!wasOpen || range !== this.chartRange) this.loadChartRange(range, series);
  }

  private hideChartModal(series: ChartSeries): void {
    if (series === 'solar' && this.solarChartOpen)       { this.solarChartOpen = false;   unlockScroll(); }
    if (series === 'froeling' && this.heizungChartOpen) { this.heizungChartOpen = false; unlockScroll(); }
  }

  selectLang(code: string): void {
    if (!this.supportedLangs.has(code)) return;
    this.closeLangPicker();
    this.switchLang(code);
  }

  switchLang(lang: string): void {
    localStorage.setItem('lang', lang);
    this.translate.use(lang);
  }

  /**
   * Log grid columns. Log entries are posted by any browser (POST /api/logs is public): every renderer
   * builds elements with textContent, never HTML from an entry. Below 768 px one wrapping "entry" column
   * (time · level · class · version, the message, the stack line) replaces the six columns.
   */
  private buildLogColDefs(): void {
    const t = (key: string) => this.translate.instant(key);
    if (this.logCompact) {
      this.colDefs = [{
        colId: 'entry', field: 'ts', headerName: t('ADMIN_LOG_ENTRY'), flex: 1, sortable: true, sort: 'desc',
        wrapText: true, autoHeight: true, filter: 'agTextColumnFilter',   // a text filter, not the number filter of "ts"
        filterValueGetter: p => [p.data?.message, p.data?.className, this.logLevelLabel(p.data?.level), p.data?.stack]
          .filter(Boolean).join('\n'),
        cellRenderer: (p: ICellRendererParams) => this.logEntryEl(p.data),
        // Only a tap on the stack line expands it (the message stays selectable)
        onCellClicked: (p: CellClickedEvent) => {
          if ((p.event?.target as Element | null)?.closest?.('.log-stack')) this.toggleLogStack(p);
        },
      }];
      return;
    }
    this.colDefs = [
      {
        // Own colId: a column set switch must not reuse the compact "entry" column (same field)
        colId: 'ts', field: 'ts', headerName: t('COLS.ZEIT'), sortable: true,
        valueFormatter: p => this.fmtTs(p.value),
        sort: 'desc',
      },
      {
        field: 'level', headerName: t('COLS.LEVEL'), sortable: true, filter: true, width: 120,
        filterValueGetter: p => this.logLevelLabel(p.data?.level),
        cellRenderer: (p: ICellRendererParams) => this.logLevelEl(p.value),
      },
      {
        field: 'version', headerName: t('COLS.VERSION'),
        valueFormatter: p => (p.value ?? '?').slice(0, 7),
      },
      // A long class name or stack line cannot squeeze the message to nothing (capped / fixed widths)
      { field: 'className', headerName: t('COLS.KLASSE'), sortable: true, filter: true, maxWidth: 220 },
      {
        field: 'message', headerName: t('COLS.NACHRICHT'), flex: 1, minWidth: 240, filter: true,
        wrapText: true, autoHeight: true,
      },
      {
        field: 'stack', headerName: t('COLS.STACK'), filter: true, width: 200,
        autoHeight: true,
        cellRenderer: (p: ICellRendererParams) => p.value ? this.logStackEl(p.data) : this.logTextEl('span', 'text-muted', '–'),
        onCellClicked: (p: CellClickedEvent) => this.toggleLogStack(p),
      },
    ];
  }

  /** Translated level name ("Hoch" / "High"); an unknown level as it came. */
  private logLevelLabel(level: unknown): string {
    const btn = LOG_LEVEL_BTNS.find(b => b.key !== 'all' && b.key === level);
    return btn ? this.translate.instant(btn.label) : String(level ?? '');
  }

  private logTextEl<K extends keyof HTMLElementTagNameMap>(tag: K, cls: string, text: string): HTMLElementTagNameMap[K] {
    const el = document.createElement(tag);
    if (cls) el.className = cls;
    el.textContent = text;
    return el;
  }

  /** `meta`: in an 11 px meta line the badge gets 11 px too (.badge alone is .75em, ~8 px there). */
  private logLevelEl(level: unknown, meta = false): HTMLElement {
    return this.logTextEl('span', `badge bg-${this.levelBadge(String(level ?? ''))}${meta ? ' dash-meta' : ''}`, this.logLevelLabel(level));
  }

  /** Stack cell: its first line (▶), or the whole stack (▲) once tapped. */
  private logStackEl(entry: any): HTMLElement {
    const stack = String(entry?.stack ?? '');
    if (this.expandedStackRows.has(entry)) {
      const pre = this.logTextEl('pre', 'log-stack dash-meta m-0', '▲ ' + stack);
      pre.style.whiteSpace = 'pre-wrap';
      pre.style.wordBreak  = 'break-all';
      pre.style.cursor     = 'pointer';
      pre.title = this.translate.instant('STACK_EINKLAPPEN');
      return pre;
    }
    const line = this.logTextEl('span', 'log-stack d-block text-truncate font-monospace dash-meta', '▶ ' + stack.split('\n')[0]);
    line.style.cursor = 'pointer';
    line.title = stack;
    return line;
  }

  /** Compact log row (phones): time · level · class · version, the message (wraps), the stack line. */
  private logEntryEl(entry: any): HTMLElement {
    const root = this.logTextEl('div', 'py-1 lh-sm', '');
    const meta = this.logTextEl('div', 'd-flex flex-wrap align-items-center column-gap-2 dash-meta text-body-secondary', '');
    meta.append(this.logTextEl('span', 'text-nowrap', this.fmtTs(entry?.ts)), this.logLevelEl(entry?.level, true));
    if (entry?.className) meta.append(this.logTextEl('span', 'text-break', String(entry.className)));
    if (entry?.version)   meta.append(this.logTextEl('span', 'font-monospace', String(entry.version).slice(0, 7)));
    root.append(meta, this.logTextEl('div', 'small text-break mt-1', String(entry?.message ?? '')));
    if (entry?.stack) root.append(this.logStackEl(entry));
    return root;
  }

  private toggleLogStack(p: CellClickedEvent): void {
    const entry = p.data;
    if (!entry?.stack) return;
    if (!this.expandedStackRows.delete(entry)) this.expandedStackRows.add(entry);
    p.api.redrawRows({ rowNodes: [p.node] });
  }

  /** Admin routes accept only the session token; the password is sent to /api/admin/login alone. */
  private adminHeaders(): Record<string, string> {
    return { 'X-Session-Token': this.adminToken };
  }

  /** Same headers for child sections (the KNX bus monitor fetches on its own). */
  readonly adminHeadersFn = (): Record<string, string> => this.adminHeaders();

  /** A child section got a 401: drop the session like fetchLogs() does. */
  onAdminUnauthorized(): void {
    this.handleAuthError();
  }

  /** KNX tab: the admin headers while a session token is held (the room charts then show their KNX
   *  details), else null — public visitors never request them. */
  get knxAdminAuth(): (() => Record<string, string>) | null {
    return this.adminToken ? this.adminHeadersFn : null;
  }

  /** The KNX tab's details got a 401: the held token is stale — drop it quietly (no password error),
   *  like the auto-login check in toggleAdmin(). */
  onKnxAdminUnauthorized(): void {
    if (!this.adminToken) return;
    this.adminToken          = '';
    this.logAuthenticated    = false;
    this.adminLegacyPassword = false;
    localStorage.removeItem('adminSessionToken');
  }

  private handleAuthError(): void {
    this.logAuthError     = true;
    this.logThrottled     = false;
    this.logLoginError    = null;
    this.logAuthenticated = false;
    this.adminLegacyPassword = false;
    this.logCode          = '';
    // Token is invalid — clear it so the password prompt shows
    if (this.adminToken) {
      this.adminToken = '';
      localStorage.removeItem('adminSessionToken');
    }
    this.fetchLoginInfo();
  }

  fetchLogs(): void {
    const param = this.logLevel === 'all' ? '' : `?level=${this.logLevel}`;
    fetch(`/api/logs${param}`, { headers: this.adminHeaders() })
      .then(r => {
        if (r.status === 401) { this.handleAuthError(); this.logs = []; return null; }
        this.logAuthError     = false;
        this.logAuthenticated = true;
        return r.json();
      })
      .then(data => { if (data) this.logs = (data as any[]).reverse(); })
      .catch(() => {});
  }

  setLogLevel(level: LogLevel): void {
    this.link.set('admin.level', level === 'all' ? null : level);
    this.logLevel = level;
    this.fetchLogs();
  }

  fetchClients(): void {
    fetch('/api/clients', { headers: this.adminHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(data => { this.clients = (data as any[]).sort((a, b) => a.connectedAt - b.connectedAt); })
      .catch(() => {});
  }

  /** Footer ⚙️ button. */
  toggleAdmin(): void {
    if (this.adminOpen) this.closeAdmin();
    else this.openAdmin();
  }

  /** Open the admin overlay; the link names its section (admin=<section>) and log level (admin.level).
   *  Credentials and the session token never go into the link. */
  openAdmin(): void {
    if (!this.adminOpen) pushSheetEntry(SHEET_ADMIN, this.link, this.zone);
    this.link.set('admin', this.adminSection);
    this.link.set('admin.level', this.logLevel === 'all' ? null : this.logLevel);
    this.showAdmin();
  }

  closeAdmin(): void {
    this.link.set('admin', null);
    this.link.clearPrefix('admin.');   // level and the sections' own keys (admin.knx…, admin.cams…)
    this.hideAdmin();
    leaveSheetEntry(SHEET_ADMIN);
  }

  /** The link names the admin overlay: open it with its section / level (reads only — the GETs an
   *  opening does anyway). Without a session the login form shows and the keys stay: the section
   *  and level apply after the login. */
  private applyAdminLink(): void {
    const section = this.link.get('admin');
    if (section == null) { this.hideAdmin(); return; }
    const level = this.link.get('admin.level');
    const logLevel: LogLevel = LOG_LEVELS.includes(level as LogLevel) ? level as LogLevel : 'all';
    const levelChanged = logLevel !== this.logLevel;
    this.logLevel = logLevel;
    const next: AdminSection = ADMIN_SECTIONS.includes(section as AdminSection) ? section as AdminSection : this.adminSection;
    if (!this.adminOpen) {
      this.adminSection = next;   // before opening: the auto-login fetches for it
      this.showAdmin();
    } else if (next !== this.adminSection) {
      this.showAdminSection(next);
    } else if (levelChanged && next === 'logs' && this.logAuthenticated) {
      this.fetchLogs();
    }
  }

  private showAdmin(): void {
    if (this.adminOpen) return;
    this.adminOpen = true;
    lockScroll();
    this.fetchLoginInfo();
    // Auto-login if a stored token exists
    if (!this.logAuthenticated && this.adminToken) {
      fetch('/api/admin/check', { headers: { 'X-Session-Token': this.adminToken } })
        .then(r => {
          if (r.ok) {
            this.logAuthenticated = true;
            this.fetchAdminStatus();
            if (this.adminSection === 'logs')    this.fetchLogs();
            if (this.adminSection === 'clients') this.fetchClients();
          } else {
            this.adminToken = '';
            localStorage.removeItem('adminSessionToken');
          }
        }).catch(() => {});
    } else if (this.logAuthenticated) {
      if (this.adminSection === 'logs')    this.fetchLogs();
      if (this.adminSection === 'clients') this.fetchClients();
    }
  }

  private hideAdmin(): void {
    if (this.adminOpen) { this.adminOpen = false; unlockScroll(); this.expandedStackRows.clear(); }
  }

  setAdminSection(section: AdminSection): void {
    this.link.set('admin', section);
    this.showAdminSection(section);
  }

  private showAdminSection(section: AdminSection): void {
    this.adminSection = section;
    if (section === 'logs'    && this.logAuthenticated) this.fetchLogs();
    if (section === 'clients' && this.logAuthenticated) this.fetchClients();
  }

  /** Public GET /api/admin/login → {totp}: show the code field? (Nothing else is revealed there.) */
  private fetchLoginInfo(): void {
    fetch('/api/admin/login', { cache: 'no-store' })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data && typeof data.totp === 'boolean') this.adminTotp = data.totp; })
      .catch(() => {});
  }

  /** The code field keeps digits only (max. 6); a complete code with a password typed submits right away. */
  onLogCodeInput(el: HTMLInputElement): void {
    const v = el.value.replace(/\D/g, '').slice(0, 6);
    if (el.value !== v) el.value = v;
    this.logCode = v;
    if (v.length === 6 && this.logPassword && !this.logLoggingIn) this.submitLogPassword();
  }

  submitLogPassword(): void {
    if (!this.logPassword || this.logLoggingIn) return;
    // TOTP known to be required: only a complete code is worth a (throttled) attempt
    if (this.adminTotp === true && !/^\d{6}$/.test(this.logCode)) { document.getElementById('adminCode')?.focus(); return; }
    const body: { password: string; code?: string } = { password: this.logPassword };
    if (this.adminTotp !== false && this.logCode) body.code = this.logCode;
    this.logLoggingIn = true;
    // JSON body (UTF-8): a header could not carry characters above U+00FF (€, „“, –, …)
    fetch('/api/admin/login', {
      method: 'POST', cache: 'no-store',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(async r => {
        if (r.status === 429) { this.logThrottled = true; this.logAuthError = false; this.logLoginError = null; this.logCode = ''; return null; }
        // Wrong password or code (the backend does not say which)
        if (r.status === 401) { this.handleAuthError(); return null; }
        if (!r.ok) {
          // Not the credentials: ADMIN_PW_HASH / TOTP secret malformed (503, fail closed), login pool busy (503), …
          const text = await r.text().catch(() => '');
          const totpVar = /\b(?:ADMIN|CAMS)_TOTP_SECRET\b/.exec(text)?.[0];   // the variable the backend names (never a value)
          this.logAuthError  = false;
          this.logThrottled  = false;
          this.logLoginError = text.includes('ADMIN_PW_HASH')  ? { key: 'ADMIN_LOGIN_PW_HASH_INVALID', msg: '' }
                             : totpVar                         ? { key: 'ADMIN_LOGIN_TOTP_INVALID', msg: totpVar }
                             : { key: 'ADMIN_LOGIN_UNAVAILABLE', msg: `HTTP ${r.status}` };
          return null;
        }
        return r.json();
      })
      .then(data => {
        if (!data?.token) return;
        this.adminToken = data.token;
        localStorage.setItem('adminSessionToken', data.token);
        // Admin routes take only the session token: password and code are not kept around
        this.logPassword      = '';
        this.logCode          = '';
        this.logAuthError     = false;
        this.logThrottled     = false;
        this.logLoginError    = null;
        this.logAuthenticated = true;
        if (typeof data.legacyPassword === 'boolean') this.adminLegacyPassword = data.legacyPassword;
        if (typeof data.totp === 'boolean') this.adminTotp = data.totp;
        this.fetchAdminStatus();
        this.fetchLogs();
        this.fetchClients();
      })
      .catch(e => {
        this.logAuthError  = false;
        this.logThrottled  = false;
        this.logLoginError = { key: 'ADMIN_LOGIN_UNAVAILABLE', msg: e?.message || String(e) };
      })
      .finally(() => { this.logLoggingIn = false; });
  }

  /** Reads `legacyPassword` from the camera status (admin session only) for the header banner. */
  private fetchAdminStatus(): void {
    if (!this.adminToken) return;
    fetch('/api/admin/cams/status', { cache: 'no-store', headers: this.adminHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data && typeof data.legacyPassword === 'boolean') this.adminLegacyPassword = data.legacyPassword; })
      .catch(() => {});
  }

  adminLogout(): void {
    fetch('/api/admin/logout', { method: 'POST', headers: { 'X-Session-Token': this.adminToken } })
      .catch(() => {});
    this.adminToken       = '';
    this.logAuthenticated = false;
    this.logPassword      = '';
    this.logCode          = '';
    this.logAuthError     = false;
    this.logThrottled     = false;
    this.logLoginError    = null;
    this.adminLegacyPassword = false;
    this.logs             = [];
    this.clients          = [];
    localStorage.removeItem('adminSessionToken');
  }

  fmtTs(ts: number | string): string {
    return new Date(Number(ts)).toLocaleString(uiLocale(), { dateStyle: 'short', timeStyle: 'medium' });
  }

  levelBadge(level: string): string {
    return level === 'high' ? 'danger' : level === 'medium' ? 'warning' : 'secondary';
  }

  shortHash(hash: string): string {
    if (!hash || hash.length <= 9) return hash || '…';
    return hash.slice(0, 4) + '…' + hash.slice(-4);
  }

  get todayStat(): DayStat | null {
    const today = todayLocalISO();
    return this.stats.find(s => s.date === today) ?? null;
  }

  private static readonly WASTE_ICONS: Record<string, string> = {
    RM:  'assets/waste/rm.svg',
    Bio: 'assets/waste/bio.svg',
    GT:  'assets/waste/gt.svg',
    PPK: 'assets/waste/ppk.svg',
    A:   'assets/waste/astwerk.svg',
    SoH: 'assets/waste/soh.svg',
    TB:  'assets/waste/tb.svg',
  };

  private _computeNextPickups(pickups: WastePickup[]): { type: string; name: string; date: string; icon: string }[] {
    const today = todayLocalISO();
    const map   = new Map<string, { type: string; name: string; date: string; icon: string }>();
    for (const p of pickups) {
      if (p.type === 'SoG') continue;
      if (p.date < today)  continue;
      if (!map.has(p.type)) {
        map.set(p.type, {
          type: p.type,
          name: p.name,
          date: p.date,
          icon: AppComponent.WASTE_ICONS[p.type] ?? 'assets/waste/rm.svg',
        });
      }
    }
    return [...map.values()].sort((a, b) => a.date.localeCompare(b.date));
  }
}
